import './Layout.css';

import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import AssuredWorkloadIcon from '@mui/icons-material/AssuredWorkload';
import BallotIcon from '@mui/icons-material/Ballot';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CheckIcon from '@mui/icons-material/Check';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DashboardIcon from '@mui/icons-material/Dashboard';
import FmdBadIcon from '@mui/icons-material/FmdBad';
import LockIcon from '@mui/icons-material/Lock';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import WidgetsIcon from '@mui/icons-material/Widgets';
import WorkIcon from '@mui/icons-material/Work';
import {
  Box,
  Button,
  Container,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  Typography
} from '@mui/material';
import { BR, ES, US } from 'country-flag-icons/react/3x2';
import MuiAppBar from '@mui/material/AppBar';
import MuiDrawer from '@mui/material/Drawer';
import { styled, useTheme } from '@mui/material/styles';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';

import { hasAnyRole, ROUTE_ROLES } from '../config/routeAccess';
import { getUserInfo, logoutSuccess, setUserData } from '../redux/userSlice';
import { clearAuthData, getUserData as getStoredUserData, isAuthenticated, setStoredUserPreferredLanguage } from '../utils/tokenStorage';
import { useConfirm } from './ConfirmProvider';

let user = {};

const LANG_OPTIONS = [
  { lng: 'pt-BR', Flag: BR, labelKey: 'langPt' },
  { lng: 'en', Flag: US, labelKey: 'langEn' },
  { lng: 'es', Flag: ES, labelKey: 'langEs' }
];

const FLAG_BUTTON_STYLE = { width: 24, height: 16, borderRadius: 2, display: 'block' };
const FLAG_MENU_STYLE = { width: 20, height: 14, borderRadius: 2, display: 'block' };

function Layout({ children }) {
  const { t, i18n } = useTranslation();
  const { confirm } = useConfirm();
  const navigate = useNavigate();
  const location = useLocation();
  const authenticated = isAuthenticated();
  const showGuestBar = !authenticated && location.pathname !== '/login';
  const currentLng = i18n.language === 'pt' ? 'pt-BR' : i18n.language;
  const CurrentFlag = LANG_OPTIONS.find((option) => option.lng === currentLng)?.Flag ?? BR;
  const [open, setOpen] = useState(false);
  const [profileAnchor, setProfileAnchor] = useState(null);
  const [languageAnchor, setLanguageAnchor] = useState(null);

  const drawerWidth = 240;
  const theme = useTheme();

  const dispatch = useDispatch();

  const userData = useSelector(getUserInfo);

  useEffect(() => {
    const storedUserData = getStoredUserData();
    if (storedUserData) {
      dispatch(setUserData(storedUserData));
      if (storedUserData.preferredLanguage) {
        i18n.changeLanguage(storedUserData.preferredLanguage);
      }
    }
  }, [dispatch, i18n]);

  const checkUserPermissions = (item) => hasAnyRole(userData?.roles, item.roles);

  const drawerItems = [
    { labelKey: 'nav.dashboard', icon: <DashboardIcon />, route: '/dashboard', roles: ROUTE_ROLES.dashboard },
    { labelKey: 'nav.questionaries', icon: <BallotIcon />, route: '/questionaries', roles: ROUTE_ROLES.questionaries },
    { labelKey: 'nav.localAgenda', icon: <PendingActionsIcon />, route: '/localplan', roles: ROUTE_ROLES.localplan },
    { labelKey: 'nav.sponsors', icon: <AssuredWorkloadIcon />, route: '/sponsors', roles: ROUTE_ROLES.sponsors },
    { labelKey: 'nav.opportunities', icon: <WorkIcon />, route: '/opportunities', roles: ROUTE_ROLES.opportunities },
    { labelKey: 'nav.projects', icon: <CalendarMonthIcon />, route: '/projects', roles: ROUTE_ROLES.projects },
    { labelKey: 'nav.counties', icon: <FmdBadIcon />, route: '/counties', roles: ROUTE_ROLES.counties },
    { labelKey: 'nav.questionaryManagement', icon: <WidgetsIcon />, route: '/questionary-management', roles: ROUTE_ROLES.questionaryManagement },
    { labelKey: 'nav.questions', icon: <QuestionAnswerIcon />, route: '/questions', roles: ROUTE_ROLES.questions },
    { labelKey: 'nav.admin', icon: <AdminPanelSettingsIcon />, route: '/admin', roles: ROUTE_ROLES.admin }
  ];

  const handleLogout = async () => {
    setProfileAnchor(null);
    const ok = await confirm({
      title: t('appBar.logout'),
      message: t('appBar.confirmLogout'),
      confirmButtonText: t('appBar.logout'),
      cancelButtonText: t('common.cancel'),
      confirmButtonColor: 'error'
    });
    if (!ok) return;
    dispatch(logoutSuccess());
    clearAuthData();
    navigate('/login');
  };

  const handleCloseProfile = () => setProfileAnchor(null);
  const handleOpenProfile = (e) => setProfileAnchor(e.currentTarget);
  const handleCloseLanguage = () => setLanguageAnchor(null);
  const handleOpenLanguage = (e) => setLanguageAnchor(e.currentTarget);

  const openedMixin = (theme) => ({
    width: drawerWidth,
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen
    }),
    overflowX: 'hidden'
  });

  const closedMixin = (theme) => ({
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen
    }),
    overflowX: 'hidden',
    width: `calc(${theme.spacing(7)} + 1px)`,
    [theme.breakpoints.up('sm')]: {
      width: `calc(${theme.spacing(6)} + 1px)`
    }
  });

  const DrawerHeader = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    padding: theme.spacing(0, 1),
    // necessary for content to be below app bar
    ...theme.mixins.toolbar
  }));

  const AppBar = styled(MuiAppBar, {
    shouldForwardProp: (prop) => prop !== 'open'
  })(({ theme }) => ({
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen
    }),
    variants: [
      {
        props: ({ open }) => open,
        style: {
          marginLeft: drawerWidth,
          width: `calc(100% - ${drawerWidth}px)`,
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen
          })
        }
      }
    ]
  }));

  const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(({ theme }) => ({
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    variants: [
      {
        props: ({ open }) => open,
        style: {
          ...openedMixin(theme),
          '& .MuiDrawer-paper': openedMixin(theme)
        }
      },
      {
        props: ({ open }) => !open,
        style: {
          ...closedMixin(theme),
          '& .MuiDrawer-paper': closedMixin(theme)
        }
      }
    ]
  }));

  return (
    <>
      <Box sx={{ display: 'flex' }}>
        {!!userData?.roles && (
          <>
            <AppBar className="app-bar" position="fixed" open={open}>
              <Toolbar sx={{ minHeight: { xs: 56, sm: 64 } }}>
                <IconButton
                  color="inherit"
                  aria-label="open drawer"
                  onClick={() => setOpen(true)}
                  edge="start"
                  sx={[{ marginRight: 2 }, open && { display: 'none' }]}
                >
                  <MenuIcon />
                </IconButton>

                <Typography variant="h6" component="div">
                  <img src="/logo.png" alt="Logo" className="app-bar-logo" />
                </Typography>
                <Box sx={{ flex: 1, minWidth: 0 }} aria-hidden />

                {authenticated ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }} component="nav">
                    <IconButton color="inherit" onClick={handleOpenProfile} aria-label={t('appBar.profile')} size="medium">
                      <AccountCircleIcon />
                    </IconButton>
                    <Menu
                      anchorEl={profileAnchor}
                      open={Boolean(profileAnchor)}
                      onClose={handleCloseProfile}
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                      transformOrigin={{ vertical: 'top', horizontal: 'left ' }}
                      slotProps={{ paper: { sx: { maxWidth: 380, right: 69, top: '50px !important', left: 'initial !important' } } }}
                    >
                      <Box sx={{ px: 2, py: 2, right: 50, top: 50 }}>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          {t('profile.userInfo')}
                        </Typography>
                        <Typography variant="body2" noWrap title={userData?.name}>
                          <strong>{t('profile.name')}:</strong> {userData?.name ?? '—'}
                        </Typography>
                        <Typography variant="body2" noWrap title={userData?.email}>
                          <strong>{t('profile.email')}:</strong> {userData?.email ?? '—'}
                        </Typography>
                        {Array.isArray(userData?.roles) && userData.roles.length > 0 && (
                          <Typography variant="body2" sx={{ mt: 0.5 }}>
                            <strong>{t('profile.roles')}:</strong> {userData.roles.join(', ')}
                          </Typography>
                        )}
                      </Box>
                      <Divider />
                      <Typography variant="caption" sx={{ px: 2, pt: 1, display: 'block', color: 'text.secondary' }}>
                        {t('language')}
                      </Typography>
                      {[
                        { lng: 'pt-BR', labelKey: 'langPt' },
                        { lng: 'en', labelKey: 'langEn' },
                        { lng: 'es', labelKey: 'langEs' }
                      ].map(({ lng, labelKey }) => {
                        const currentLng = i18n.language === 'pt' ? 'pt-BR' : i18n.language;
                        const isSelected = currentLng === lng;
                        return (
                          <MenuItem
                            key={lng}
                            onClick={() => {
                              i18n.changeLanguage(lng);
                              setStoredUserPreferredLanguage(lng);
                              const data = getStoredUserData();
                              if (data) {
                                dispatch(setUserData({ ...data, preferredLanguage: lng }));
                              }
                              handleCloseProfile();
                            }}
                            selected={isSelected}
                            sx={isSelected ? { bgcolor: 'action.selected' } : {}}
                          >
                            {isSelected ? (
                              <ListItemIcon sx={{ minWidth: 36 }}>
                                <CheckIcon color="primary" fontSize="small" />
                              </ListItemIcon>
                            ) : (
                              <ListItemIcon sx={{ minWidth: 36 }} />
                            )}
                            <ListItemText>{t(labelKey)}</ListItemText>
                          </MenuItem>
                        );
                      })}
                      <Divider />
                      <MenuItem
                        component={RouterLink}
                        to="/settings"
                        onClick={handleCloseProfile}
                      >
                        <ListItemIcon>
                          <LockIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>{t('profile.changePassword')}</ListItemText>
                      </MenuItem>
                      <Divider />
                      <MenuItem onClick={handleLogout}>
                        <ListItemIcon>
                          <LogoutIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>{t('appBar.logout')}</ListItemText>
                      </MenuItem>
                    </Menu>
                    <IconButton color="inherit" onClick={handleLogout} aria-label={t('appBar.logout')} size="medium">
                      <LogoutIcon />
                    </IconButton>
                  </Box>
                ) : (
                  <Box sx={{ marginLeft: 'auto' }}>
                    <Button color="inherit" component={RouterLink} to="/login">
                      {t('appBar.login')}
                    </Button>
                  </Box>
                )}
              </Toolbar>
            </AppBar>
            <Drawer variant="permanent" open={open} onClose={() => setOpen(false)}>
              <DrawerHeader>
                <IconButton onClick={() => setOpen(false)}>{theme.direction === 'rtl' ? <ChevronRightIcon /> : <ChevronLeftIcon />}</IconButton>
              </DrawerHeader>
              <Divider />
              <List>
                {drawerItems
                  .filter((item) => checkUserPermissions(item))
                  .map((item, index) => (
                    <ListItem
                      key={item.route}
                      disablePadding
                      sx={{ display: 'block' }}
                      component={RouterLink}
                      to={item.route}
                      onClick={() => setOpen(false)}
                    >
                      <ListItemButton sx={[{ minHeight: 36, px: 2.5 }, open ? { justifyContent: 'initial' } : { justifyContent: 'center' }]}>
                        <ListItemIcon sx={[{ minWidth: 0, justifyContent: 'center' }, open ? { mr: 3 } : { mr: 'auto' }]}>{item.icon}</ListItemIcon>
                        <ListItemText primary={t(item.labelKey)} sx={[open ? { opacity: 1 } : { opacity: 0 }]} />
                      </ListItemButton>
                    </ListItem>
                  ))}
              </List>
            </Drawer>
          </>
        )}
        {showGuestBar && (
          <AppBar className="app-bar" position="fixed">
            <Toolbar sx={{ minHeight: { xs: 56, sm: 64 } }}>
              <Typography variant="h6" component="div">
                <img src="/logo.png" alt="Logo" className="app-bar-logo" />
              </Typography>
              <Box sx={{ flex: 1 }} />
              <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
                <IconButton color="inherit" onClick={handleOpenLanguage} aria-label={t('language')} size="medium">
                  <CurrentFlag style={FLAG_BUTTON_STYLE} />
                </IconButton>
                <Menu
                  anchorEl={languageAnchor}
                  open={Boolean(languageAnchor)}
                  onClose={handleCloseLanguage}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                  slotProps={{
                    paper: {
                      sx: {
                        minWidth: 180,
                        right: 8,
                        top: { xs: '56px !important', sm: '64px !important' },
                        left: 'auto !important'
                      }
                    }
                  }}
                >
                {LANG_OPTIONS.map(({ lng, Flag, labelKey }) => {
                  const isSelected = currentLng === lng;
                  return (
                    <MenuItem
                      key={lng}
                      onClick={() => {
                        i18n.changeLanguage(lng);
                        handleCloseLanguage();
                      }}
                      selected={isSelected}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <Flag style={FLAG_MENU_STYLE} />
                      </ListItemIcon>
                      <ListItemText>{t(labelKey)}</ListItemText>
                      {isSelected && <CheckIcon color="primary" fontSize="small" />}
                    </MenuItem>
                  );
                })}
                </Menu>
              </Box>
            </Toolbar>
          </AppBar>
        )}
        {/* <Container sx={{ flexGrow: 1, p: 3 }} maxWidth="xl">
          <DrawerHeader /> {children}
        </Container> */}
        <Container style={{ flexGrow: 1, marginTop: '2rem', padding: '0' }} maxWidth="xl" onClick={() => setOpen(false)}>
          {(!!userData?.roles || showGuestBar) && <DrawerHeader />}
          {children}
        </Container>
      </Box>
    </>
  );
}

export default Layout;
